import React, { useEffect, useRef, useState } from 'react';
import { StyleSheet, Switch, Text, View } from 'react-native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppInput } from '../../components/AppInput';
import { AppScreen } from '../../components/AppScreen';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  createLeaveConfiguration,
  updateLeaveConfiguration,
} from '../../services/leaveConfigurationService';
import { getLeaveSessionTypes } from '../../services/leaveService';
import type { LeaveDurationOption } from '../../types/leave';
import type {
  LeaveAccrualBasis,
  LeaveConfigurationDurationRule,
} from '../../types/leaveConfiguration';
import { formatDisplayLabel } from '../../utils/camelCaseLabel';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { leaveErrorMessage } from '../../utils/leaveErrors';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<RootStackParamList, 'LeaveConfigurationCreate'>;
export type DurationRuleForm = {
  durationCode: string;
  durationName: string;
  isHalfDay: boolean;
  requiresExplicitRule: boolean;
  isAllowed: boolean;
  minimumAdvanceDays: string;
  maximumRequestsPerMonth: string;
};
type Form = {
  leaveTypeCode: string; leaveTypeName: string; description: string;
  leaveCategory: 'PAID' | 'UNPAID'; policyCode: string; policyName: string;
  effectiveFrom: string; annualEntitlement: string; monthlyAccrual: string;
  allowFullDay: boolean; allowHalfDay: boolean; requiresBalance: boolean;
  isDefault: boolean; generateBalances: boolean; generateYear: string;
  accrualBasis: LeaveAccrualBasis; carryForwardAllowed: boolean;
  maxCarryForwardDays: string; durationRules: DurationRuleForm[];
};

function toDateInput(value: string | null | undefined): string {
  if (!value) return new Date().toISOString().slice(0, 10);
  return String(value).slice(0, 10);
}

function validateConfiguredDays(value: string, label: string, maximum: number): string | null {
  const days = Number(value);
  if (!Number.isFinite(days) || days < 0 || days > maximum) {
    return `${label} must be between 0 and ${maximum}.`;
  }
  if (Math.abs(Math.round(days * 100) / 100 - days) > 1e-9) {
    return `${label} supports up to 2 decimal places.`;
  }
  return null;
}

function validateWholeNumber(
  value: string,
  label: string,
  minimum: number,
  maximum: number,
): string | null {
  const parsed = Number(value);
  if (!value.trim() || !Number.isInteger(parsed) || parsed < minimum || parsed > maximum) {
    return `${label} must be a whole number between ${minimum} and ${maximum}.`;
  }
  return null;
}

/** Merges the database duration catalog with the saved rules for this leave type. */
export function buildDurationRuleForms(
  durations: LeaveDurationOption[],
  saved: LeaveConfigurationDurationRule[] = [],
  fallback: { allowFullDay: boolean; allowHalfDay: boolean } = {
    allowFullDay: true,
    allowHalfDay: true,
  },
): DurationRuleForm[] {
  return durations.map(duration => {
    const rule = saved.find(item => item.durationCode === duration.durationCode);
    const requiresExplicitRule = duration.requiresExplicitRule === true;
    const defaultAllowed = requiresExplicitRule
      ? false
      : duration.isHalfDay
        ? fallback.allowHalfDay
        : fallback.allowFullDay;
    return {
      durationCode: duration.durationCode,
      durationName: duration.durationName,
      isHalfDay: duration.isHalfDay,
      requiresExplicitRule,
      isAllowed: rule ? rule.isAllowed : defaultAllowed,
      minimumAdvanceDays: String(rule?.minimumAdvanceDays ?? 0),
      maximumRequestsPerMonth:
        rule?.maximumRequestsPerMonth != null ? String(rule.maximumRequestsPerMonth) : '',
    };
  });
}

/** allow_full_day / allow_half_day follow the standard (non rule-only) durations. */
export function deriveDurationFlags(
  rules: DurationRuleForm[],
): { allowFullDay: boolean; allowHalfDay: boolean } {
  const standard = rules.filter(rule => !rule.requiresExplicitRule && rule.isAllowed);
  return {
    allowFullDay: standard.some(rule => !rule.isHalfDay),
    allowHalfDay: standard.some(rule => rule.isHalfDay),
  };
}

const today = new Date().toISOString().slice(0, 10);
const EMPTY: Form = {
  leaveTypeCode: '', leaveTypeName: '', description: '', leaveCategory: 'PAID',
  policyCode: '', policyName: '', effectiveFrom: today, annualEntitlement: '12',
  monthlyAccrual: '1',
  allowFullDay: true, allowHalfDay: true,
  requiresBalance: true, isDefault: true, generateBalances: true,
  generateYear: String(new Date().getFullYear()),
  accrualBasis: 'WORKING_DAYS', carryForwardAllowed: true, maxCarryForwardDays: '',
  durationRules: [],
};

export function validateLeaveConfigurationForm(form: Form, isEdit = false): string | null {
  if (!isEdit) {
    if (!form.leaveTypeCode.trim()) return 'Leave type code is required.';
    if (!/^[A-Za-z0-9_]+$/.test(form.leaveTypeCode.trim())) return 'Leave type code can contain letters, numbers, and underscores.';
    if (!form.policyCode.trim()) return 'Policy code is required.';
  }
  if (!form.leaveTypeName.trim()) return 'Leave type name is required.';
  if (!form.policyName.trim()) return 'Policy name is required.';
  if (!/^\d{4}-\d{2}-\d{2}$/.test(form.effectiveFrom)) return 'Effective date must use YYYY-MM-DD.';
  const annualError = validateConfiguredDays(form.annualEntitlement, 'Annual entitlement', 365);
  if (annualError) return annualError;
  const monthlyError = validateConfiguredDays(form.monthlyAccrual, 'Monthly accrual', 31);
  if (monthlyError) return monthlyError;
  if (form.carryForwardAllowed && form.maxCarryForwardDays.trim()) {
    const carryError = validateConfiguredDays(form.maxCarryForwardDays, 'Maximum carry forward', 365);
    if (carryError) return carryError;
  }
  if (form.durationRules.length > 0) {
    if (!form.durationRules.some(rule => rule.isAllowed)) {
      return 'Allow at least one leave duration.';
    }
    for (const rule of form.durationRules) {
      if (!rule.isAllowed) continue;
      const name = formatDisplayLabel(rule.durationName);
      const advanceError = validateWholeNumber(
        rule.minimumAdvanceDays, `${name} advance days`, 0, 365,
      );
      if (advanceError) return advanceError;
      if (rule.maximumRequestsPerMonth.trim()) {
        const limitError = validateWholeNumber(
          rule.maximumRequestsPerMonth, `${name} requests per month`, 1, 31,
        );
        if (limitError) return limitError;
      }
    }
  } else if (!form.allowFullDay && !form.allowHalfDay) {
    return 'Enable full-day or half-day leave.';
  }
  if (!isEdit && form.generateBalances) {
    const year = Number(form.generateYear);
    if (!Number.isInteger(year) || year < 2000 || year > 2200) return 'Balance year is invalid.';
  }
  return null;
}

function Row({ label, value, onValueChange }: { label: string; value: boolean; onValueChange: (value: boolean) => void }) {
  const palette = useAppColors();
  return <View style={styles.switchRow}><Text style={[styles.switchLabel, { color: palette.textPrimary }]}>{label}</Text><Switch value={value} onValueChange={onValueChange} /></View>;
}

export function LeaveConfigurationCreateScreen({ navigation, route }: Props) {
  const palette = useAppColors();
  const existing = route.params?.configuration;
  const isEdit = existing != null;
  const [form, setForm] = useState<Form>(() => (
    existing
      ? {
          leaveTypeCode: existing.leaveTypeCode,
          leaveTypeName: existing.leaveTypeName,
          description: existing.description || '',
          leaveCategory: existing.leaveCategory === 'UNPAID' ? 'UNPAID' : 'PAID',
          policyCode: existing.policyCode,
          policyName: existing.policyName,
          effectiveFrom: toDateInput(existing.effectiveFrom),
          annualEntitlement: String(existing.annualEntitlement),
          monthlyAccrual: String(existing.monthlyAccrual ?? 0),
          allowFullDay: existing.allowFullDay,
          allowHalfDay: existing.allowHalfDay,
          requiresBalance: existing.requiresBalance,
          isDefault: existing.isDefault,
          generateBalances: false,
          generateYear: String(new Date().getFullYear()),
          accrualBasis: existing.accrualBasis === 'FIXED_MONTHLY' ? 'FIXED_MONTHLY' : 'WORKING_DAYS',
          carryForwardAllowed: existing.carryForwardAllowed !== false,
          maxCarryForwardDays:
            existing.maxCarryForwardDays != null ? String(existing.maxCarryForwardDays) : '',
          durationRules: [],
        }
      : EMPTY
  ));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [durationsError, setDurationsError] = useState<string | null>(null);
  const lock = useRef(false);
  const set = <K extends keyof Form>(key: K, value: Form[K]) => setForm(current => ({ ...current, [key]: value }));
  const setRule = (durationCode: string, patch: Partial<DurationRuleForm>) =>
    setForm(current => ({
      ...current,
      durationRules: current.durationRules.map(rule =>
        rule.durationCode === durationCode ? { ...rule, ...patch } : rule,
      ),
    }));

  useEffect(() => {
    let cancelled = false;
    getLeaveSessionTypes()
      .then(durations => {
        if (cancelled) return;
        setForm(current => ({
          ...current,
          durationRules: buildDurationRuleForms(durations, existing?.durationRules ?? [], {
            allowFullDay: current.allowFullDay,
            allowHalfDay: current.allowHalfDay,
          }),
        }));
      })
      .catch(() => {
        if (!cancelled) setDurationsError('Note: Leave durations could not be loaded; only full-day and half-day switches are available.');
      });
    return () => {
      cancelled = true;
    };
  }, [existing?.durationRules]);

  async function submit() {
    if (lock.current) return;
    const validation = validateLeaveConfigurationForm(form, isEdit);
    if (validation) { setError(validation); return; }
    lock.current = true; setSaving(true); setError(null);
    const flags = form.durationRules.length > 0
      ? deriveDurationFlags(form.durationRules)
      : { allowFullDay: form.allowFullDay, allowHalfDay: form.allowHalfDay };
    const policy = {
      leaveTypeName: form.leaveTypeName.trim(),
      description: form.description.trim() || null,
      leaveCategory: form.leaveCategory,
      allowFullDay: flags.allowFullDay,
      allowHalfDay: flags.allowHalfDay,
      isPaid: form.leaveCategory === 'PAID',
      requiresBalance: form.requiresBalance,
      policyName: form.policyName.trim(),
      effectiveFrom: form.effectiveFrom,
      annualEntitlement: Number(form.annualEntitlement),
      monthlyAccrual: Number(form.monthlyAccrual),
      isDefault: form.isDefault,
      accrualBasis: form.accrualBasis,
      carryForwardAllowed: form.carryForwardAllowed,
      maxCarryForwardDays:
        form.carryForwardAllowed && form.maxCarryForwardDays.trim()
          ? Number(form.maxCarryForwardDays)
          : null,
      ...(form.durationRules.length > 0
        ? {
            durationRules: form.durationRules.map(rule => ({
              durationCode: rule.durationCode,
              isAllowed: rule.isAllowed,
              minimumAdvanceDays: rule.isAllowed ? Number(rule.minimumAdvanceDays) : 0,
              maximumRequestsPerMonth:
                rule.isAllowed && rule.maximumRequestsPerMonth.trim()
                  ? Number(rule.maximumRequestsPerMonth)
                  : null,
            })),
          }
        : {}),
    };
    try {
      if (isEdit && existing) {
        await runOperation('update', () => updateLeaveConfiguration(existing.leavePolicyId, policy));
      } else {
        await runOperation('add', () => createLeaveConfiguration({
          ...policy,
          leaveTypeCode: form.leaveTypeCode.trim().toUpperCase(),
          policyCode: form.policyCode.trim().toUpperCase(),
          generateBalances: form.generateBalances,
          generateYear: form.generateBalances ? Number(form.generateYear) : undefined,
        }));
      }
      navigation.goBack();
    } catch (caught) {
      setError(leaveErrorMessage(caught, 'Unable to save leave configuration.'));
    } finally { lock.current = false; setSaving(false); }
  }

  return (
    <PermissionGuard permission={PERMISSIONS.SETTINGS_UPDATE} fallback={<UnauthorizedScreen />}>
      <AppScreen webLayout="narrow" scroll>
        <ScreenHeader title={isEdit ? 'Edit leave type' : 'Create leave type'} onBack={navigation.goBack} />
        <View style={styles.body}>
          <Text style={[styles.heading, { color: palette.textPrimary }]}>Leave type</Text>
          <AppInput label="Leave type code" value={form.leaveTypeCode} onChangeText={v => set('leaveTypeCode', v)} placeholder="PRIVILEGE_LEAVE" autoCapitalize="characters" editable={!isEdit} />
          <AppInput label="Leave type name" value={form.leaveTypeName} onChangeText={v => set('leaveTypeName', v)} placeholder="Privilege Leave" />
          <AppInput label="Description" value={form.description} onChangeText={v => set('description', v)} placeholder="Optional" />
          <View style={styles.choiceRow}>
            <AppButton title="Paid" variant={form.leaveCategory === 'PAID' ? 'primary' : 'secondary'} onPress={() => set('leaveCategory', 'PAID')} />
            <AppButton title="Unpaid" variant={form.leaveCategory === 'UNPAID' ? 'primary' : 'secondary'} onPress={() => set('leaveCategory', 'UNPAID')} />
          </View>
          {form.durationRules.length === 0 ? (
            <>
              <Row label="Allow full day" value={form.allowFullDay} onValueChange={v => set('allowFullDay', v)} />
              <Row label="Allow half day" value={form.allowHalfDay} onValueChange={v => set('allowHalfDay', v)} />
            </>
          ) : null}
          {durationsError ? <Text style={[styles.hint, { color: palette.warning }]}>{durationsError}</Text> : null}
          <Row label="Requires balance" value={form.requiresBalance} onValueChange={v => set('requiresBalance', v)} />

          {form.durationRules.length > 0 ? (
            <>
              <Text style={[styles.heading, { color: palette.textPrimary }]}>Leave durations</Text>
              <Text style={[styles.hint, { color: palette.textSecondary }]}>
                Advance days: how many days before the leave date the request must be submitted (0 = any time).
                Requests per month: leave blank for no limit.
              </Text>
              {form.durationRules.map(rule => {
                const name = formatDisplayLabel(rule.durationName);
                return (
                  <View key={rule.durationCode} style={[styles.ruleCard, { borderColor: palette.border }]}>
                    <Row label={`Allow ${name}`} value={rule.isAllowed} onValueChange={v => setRule(rule.durationCode, { isAllowed: v })} />
                    {rule.isAllowed ? (
                      <>
                        <AppInput label={`${name}: minimum advance days`} value={rule.minimumAdvanceDays} onChangeText={v => setRule(rule.durationCode, { minimumAdvanceDays: v })} keyboardType="number-pad" />
                        <AppInput label={`${name}: maximum requests per month`} value={rule.maximumRequestsPerMonth} onChangeText={v => setRule(rule.durationCode, { maximumRequestsPerMonth: v })} keyboardType="number-pad" placeholder="No limit" />
                      </>
                    ) : null}
                  </View>
                );
              })}
            </>
          ) : null}

          <Text style={[styles.heading, { color: palette.textPrimary }]}>Annual policy</Text>
          <AppInput label="Policy code" value={form.policyCode} onChangeText={v => set('policyCode', v)} placeholder="PRIVILEGE_LEAVE_DEFAULT" autoCapitalize="characters" editable={!isEdit} />
          <AppInput label="Policy name" value={form.policyName} onChangeText={v => set('policyName', v)} placeholder="Privilege Leave Policy" />
          <AppInput label="Annual entitlement (days)" value={form.annualEntitlement} onChangeText={v => set('annualEntitlement', v)} keyboardType="decimal-pad" />
          <AppInput label="Monthly accrual (days)" value={form.monthlyAccrual} onChangeText={v => set('monthlyAccrual', v)} keyboardType="decimal-pad" />
          <View style={styles.choiceRow}>
            <AppButton title="Fixed monthly" variant={form.accrualBasis === 'FIXED_MONTHLY' ? 'primary' : 'secondary'} onPress={() => set('accrualBasis', 'FIXED_MONTHLY')} />
            <AppButton title="Working days" variant={form.accrualBasis === 'WORKING_DAYS' ? 'primary' : 'secondary'} onPress={() => set('accrualBasis', 'WORKING_DAYS')} />
          </View>
          <Text style={[styles.hint, { color: palette.textSecondary }]}>
            {form.accrualBasis === 'FIXED_MONTHLY'
              ? 'The monthly job credits the monthly accrual on the first day of every month.'
              : "The monthly job credits leave from Office Calendar working days: annual entitlement × (that month's working days ÷ the year's working days). Monthly accrual is only used when annual entitlement is 0 (yearly pool = monthly accrual × 12)."}
          </Text>
          <Row label="Carry forward unused balance" value={form.carryForwardAllowed} onValueChange={v => set('carryForwardAllowed', v)} />
          {form.carryForwardAllowed ? (
            <AppInput label="Maximum carry forward to next year (days)" value={form.maxCarryForwardDays} onChangeText={v => set('maxCarryForwardDays', v)} keyboardType="decimal-pad" placeholder="No limit" />
          ) : (
            <Text style={[styles.hint, { color: palette.textSecondary }]}>
              Each month's credit must be used within that month; unused balance lapses.
            </Text>
          )}
          <AppInput label="Effective from" value={form.effectiveFrom} onChangeText={v => set('effectiveFrom', v)} placeholder="YYYY-MM-DD" />
          <Row label="Default policy" value={form.isDefault} onValueChange={v => set('isDefault', v)} />
          {!isEdit ? (
            <>
              <Row label="Generate balances for all active employees" value={form.generateBalances} onValueChange={v => set('generateBalances', v)} />
              {form.generateBalances ? <AppInput label="Balance year" value={form.generateYear} onChangeText={v => set('generateYear', v)} keyboardType="number-pad" /> : null}
            </>
          ) : null}
          {error ? <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>{error}</Text> : null}
          <AppButton title={isEdit ? 'Save leave configuration' : 'Create leave configuration'} onPress={submit} loading={saving} disabled={saving} />
        </View>
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.sm, paddingBottom: spacing.xl },
  heading: { ...typography.sectionTitle, marginTop: spacing.sm },
  hint: typography.caption,
  choiceRow: { flexDirection: 'row', gap: spacing.sm },
  switchRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', minHeight: 48 },
  switchLabel: { ...typography.body, flex: 1 },
  ruleCard: { borderWidth: StyleSheet.hairlineWidth, borderRadius: 8, padding: spacing.sm, gap: spacing.xs },
  error: typography.caption,
});
